import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import Iconify from 'src/components/iconify';
// constants
import { ROLES } from 'src/constants';
// auth
import { useAuthContext } from 'src/auth/hooks';
import { isStudentUser } from 'src/auth/student-microsoft-login';
import useOffCampusAccess from 'src/sections/off-campus/use-off-campus-access';

// ----------------------------------------------------------------------

const icon = (name) => (
  // <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  <Iconify icon={`${name}`} width={48} />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  student: icon('solar:user-bold-duotone'),
  profileReview: icon('solar:clipboard-check-bold-duotone'),
  enrollment: icon('solar:user-plus-bold-duotone'),
  assessment: icon('solar:list-check-bold-duotone'),
  vehicle: icon('solar:bus-bold-duotone'),
  part: icon('solar:accumulator-bold-duotone'),
  workOrder: icon('solar:document-add-bold-duotone'),
  purchaseOrder: icon('solar:bill-list-bold-duotone'),
  routing: icon('solar:route-bold-duotone'),
  crossboundary: icon('solar:streets-map-point-bold-duotone'),
  schoolYear: icon('solar:calendar-bold-duotone'),
  fileExport: icon('solar:export-bold-duotone'),
  careerProfile: icon('solar:feed-bold-duotone'),
  business: icon('solar:buildings-2-bold-duotone'),
  screenShare: icon('solar:screencast-bold-duotone'),
  devices: icon('solar:smartphone-2-bold-duotone'),
  careers: icon('mdi:briefcase'),
  buildings: icon('solar:buildings-bold-duotone'),
  offCampus: icon('solar:map-point-wave-bold-duotone'),
  referrals: icon('solar:inbox-in-bold-duotone'),
  placements: icon('solar:case-round-bold-duotone'),
  worksites: icon('solar:buildings-2-bold-duotone'),
  settings: icon('solar:settings-bold-duotone'),
  myDashboard: icon('solar:home-smile-bold-duotone'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();
  const student = isStudentUser(user);
  // The secretary sees Off Campus Settings only to edit the email templates
  const canEditTemplates = useOffCampusAccess().can('emailTemplates');

  // A student session (#603) sees their dashboard (#633) and nothing else — not
  // even the groups below that carry no role gate.
  const studentData = useMemo(
    () => [
      {
        subheader: t('student'),
        items: [{ title: t('My Dashboard'), path: paths.studentDashboard.root, icon: ICONS.myDashboard }],
      },
    ],
    [t]
  );

  const data = useMemo(
    () => [
      // STUDENTS
      // ----------------------------------------------------------------------
      {
        roles: [
          ROLES.SCHOOL_ADMIN,
          ROLES.USER_ROLE_COUNSELOR,
          ROLES.USER_ROLE_TEACHER,
          ROLES.USER_ROLE_SCHOOL_PRINCIPAL,
          ROLES.HOMESCHOOL_FACILITATOR,
          ROLES.HOMESCHOOL_ADMIN,
          ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR,
        ],
        subheader: 'students',
        items: [
          // The counsellor's home (#612), so first; the Students list stays below it
          {
            title: t('Careers'),
            path: '/careers-dashboard',
            icon: ICONS.careers,
            roles: [
              ROLES.USER_ROLE_SCHOOL_PRINCIPAL,
              ROLES.USER_ROLE_COUNSELOR,
              ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR,
            ],
          },
          {
            title: t('students'),
            path: paths.student.root,
            icon: ICONS.student,
            roles: [
              ROLES.SCHOOL_ADMIN,
              ROLES.USER_ROLE_COUNSELOR,
              ROLES.USER_ROLE_TEACHER,
              ROLES.USER_ROLE_SCHOOL_PRINCIPAL,
            ],
          },
          {
            title: t('students'),
            path: '#',
            icon: ICONS.student,
            roles: [ROLES.USER_ROLE_USER],
          },
          {
            title: t('profile reviews'),
            path: paths.profileReview.root,
            icon: ICONS.profileReview,
            roles: [ROLES.SCHOOL_ADMIN],
          },
          {
            title: t('home ed'),
            path: paths.homeEd.dashboard,
            icon: icon('solar:home-smile-bold-duotone'),
            roles: [ROLES.HOMESCHOOL_ADMIN, ROLES.HOMESCHOOL_FACILITATOR],
          },
          {
            title: t('enrollments'),
            path: paths.enrollment.root,
            icon: ICONS.enrollment,
            roles: ['User.Admin'],
          },
        ],
      },
      // OFF CAMPUS
      // Coordinators and the secretary; counsellors reach a student's placement
      // from the Career tab. Each list stays highlighted on its rows' own pages (`deep`).
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR, ROLES.USER_ROLE_OFF_CAMPUS_SECRETARY],
        subheader: t('off campus'),
        items: [
          { title: t('Overview'), path: paths.offCampus.root, icon: ICONS.offCampus },
          { title: t('Referrals'), path: paths.offCampus.referrals(), icon: ICONS.referrals, deep: true },
          { title: t('Placements'), path: paths.offCampus.placements(), icon: ICONS.placements, deep: true },
          { title: t('Worksites'), path: paths.offCampus.worksites(), icon: ICONS.worksites, deep: true },
          {
            title: t('Settings'),
            path: paths.offCampus.settings,
            icon: ICONS.settings,
            roles: canEditTemplates
              ? [ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR, ROLES.USER_ROLE_OFF_CAMPUS_SECRETARY]
              : [ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR],
          },
        ],
      },
      // ASSESSMENTS
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.SUPER_ADMIN],
        subheader: t('assessments'),
        items: [
          {
            title: t('WRAT5'),
            path: paths.assessments.wrat5,
            icon: ICONS.assessment,
          },
          {
            title: t('LeNS'),
            path: paths.assessments.lens,
            icon: ICONS.assessment,
          },
          {
            title: t('ADLOF'),
            path: paths.assessments.adlof,
            icon: ICONS.assessment,
          },
        ],
      },
      // ROUTING
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.USER_ROLE_TRANSPORTATION_MANAGER, ROLES.USER_ROLE_TRANSPORTATION_ADMIN],
        subheader: t('routing'),
        items: [
          {
            title: t('routing requests'),
            path: paths.routing.root,
            icon: ICONS.routing,
          },
          {
            title: t('cross boundary requests'),
            path: paths.crossboundary.root,
            icon: ICONS.crossboundary,
          },
          {
            title: t('bus routes'),
            path: paths.busRoutes.root,
            icon: ICONS.vehicle,
          },
        ],
      },
      // STUDENT PROFILE ADMIN
      // ----------------------------------------------------------------------
      {
        roles: ['studentdata.admin'],
        subheader: t('student profile admin'),
        items: [
          // SCHOOL YEARS
          {
            title: t('school years'),
            path: paths.schoolYear.root,
            icon: ICONS.schoolYear,
          },
          // FILE EXPORTS
          {
            title: t('file exports'),
            path: paths.fileExport.root,
            icon: ICONS.fileExport,
          },
        ],
      },
      // FLEET MANAGEMENT
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.USER_ROLE_FLEET_MECHANIC, ROLES.USER_ROLE_FLEET_ADMIN],
        subheader: t('fleet management'),
        items: [
          // VEHICLES
          {
            title: t('vehicles'),
            path: paths.vehicle.root,
            icon: ICONS.vehicle,
          },
          // PARTS
          {
            title: t('parts'),
            path: paths.part.root,
            icon: ICONS.part,
          },
          // WORK ORDERS
          {
            title: t('work orders'),
            path: paths.workOrder.root,
            icon: ICONS.workOrder,
          },
          {
            title: t('purchase orders'),
            path: paths.purchaseOrder.root,
            icon: ICONS.purchaseOrder,
          },
        ],
      },
      // TOOLS
      {
        roles: [ROLES.SCHOOL_ADMIN, ROLES.USER_ROLE_TEACHER, ROLES.USER_ROLE_TRANSPORTATION_ADMIN],
        // permissions: ['screenShare'],
        subheader: t('tools'),
        items: [
          {
            title: t('screen share'),
            path: paths.screenShare.share,
            icon: ICONS.screenShare,
            roles: [ROLES.USER_ROLE_TEACHER],
          },
          {
            title: t('devices'),
            path: paths.devices.root,
            icon: ICONS.devices,
            roles: [ROLES.USER_ROLE_TRANSPORTATION_ADMIN],
          },
        ],
      },
      // FACILITIES / BUILDINGS
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.BUILDINGS_ADMIN, ROLES.BUILDINGS_EDITOR, ROLES.BUILDINGS_VIEWER],
        subheader: t('facilities'),
        items: [
          {
            title: t('buildings'),
            path: paths.buildings.root,
            icon: ICONS.buildings,
            roles: [
              ROLES.BUILDINGS_ADMIN,
              ROLES.BUILDINGS_EDITOR,
              ROLES.BUILDINGS_VIEWER,
              ROLES.SUPER_ADMIN,
            ],
          },
        ],
      },
      // SCHOOL DASHBOARD
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.USER_ROLE_SCHOOL_PRINCIPAL, ROLES.SUPER_ADMIN, ROLES.USER_ROLE_FINANCE_ADMIN],
        subheader: t('school dashboard'),
        items: [
          {
            title: t('school dashboard'),
            path: '/school-dashboard',
            icon: ICONS.business,
          },
        ],
      },
      // ADMINISTRATION
      // ----------------------------------------------------------------------
      {
        roles: [ROLES.IT_ADMIN],
        subheader: t('administration'),
        items: [
          {
            title: t('employee processing'),
            path: paths.employeeProcessing.root,
            icon: icon('solar:users-group-rounded-bold-duotone'),
          },
        ],
      },
      // // STUDENT CAREERS
      // // ----------------------------------------------------------------------
      // {
      //   roles: [ROLES.SCHOOL_ADMIN],
      //   subheader: t('student careers'),
      //   items: [
      //     {
      //       title: t('career profiles'),
      //       path: paths.careers.root,
      //       icon: ICONS.careerProfile,
      //     },
      //     {
      //       title: t('businesses'),
      //       path: paths.careers.businesses,
      //       icon: ICONS.business,
      //     }
      //   ],
      // },
    ],
    [t, canEditTemplates]
  );

  return student ? studentData : data;
}
